// src/utils/tokens/dataProcessing.ts

export type RangeKey = '7d' | '30d' | '90d' | '365d' | 'All';

export const RANGE_TO_POINTS: Record<RangeKey, number> = {
  '7d': 8,
  '30d': 31,
  '90d': 91,
  '365d': 366,
  'All': 3650,
};

// Lookback needed for rolling metrics (90 days for 90d rolling metrics)
export const LOOKBACK_DAYS = 90;

export type SeriesData = { dates: number[]; values: number[] };

/**
 * Parse date string or integer to milliseconds
 */
export function parseDateToMs(d: string | number): number {
  if (typeof d === 'number') {
    if (d >= 10000101 && d <= 99991231) {
      const y = Math.floor(d / 10000);
      const m = Math.floor((d % 10000) / 100);
      const day = d % 100;
      return Date.UTC(y, m - 1, day);
    }
    return d < 1e12 ? d * 1000 : d;
  }
  const p = Date.parse(d);
  return Number.isFinite(p) ? p : 0;
}

/**
 * Compute daily returns from price series
 */
export function computeDailyReturns(aligned: number[]): number[] {
  if (!aligned.length) return [];
  const out: number[] = new Array(aligned.length).fill(NaN);
  for (let i = 1; i < aligned.length; i++) {
    const a = aligned[i];
    const b = aligned[i - 1];
    const aNum = typeof a === 'number' ? a : NaN;
    const bNum = typeof b === 'number' ? b : NaN;
    if (Number.isFinite(aNum) && Number.isFinite(bNum) && Math.abs(bNum) > 0) {
      out[i] = (aNum - bNum) / Math.abs(bNum);
    }
  }
  return out;
}

/**
 * Align time series data to common dates with optional lookback for rolling metrics
 */
export function alignSeriesData(
  historyData: Array<{ ticker: string; metrics?: Array<{ dates?: Array<string | number>; values?: (number | null)[] }> }>,
  tickers: string[],
  range: RangeKey,
  includeLookback: boolean = false
): {
  alignedSeries: Record<string, SeriesData>;
  alignedSeriesWithLookback: Record<string, SeriesData>;
  allDates: number[];
} {
  const byTicker = new Map<string, SeriesData>();
  
  // Parse raw data
  for (const tokenData of historyData) {
    const t = tokenData.ticker;
    const m = tokenData.metrics?.[0];
    if (!t || !m) continue;
    const dates = (m.dates || []).map(parseDateToMs);
    const values = (m.values || []).map((v) => (v == null ? NaN : Number(v)));
    byTicker.set(String(t).toUpperCase(), { dates, values });
  }

  // Collect all unique dates
  const dateSet = new Set<number>();
  for (const t of tickers) {
    const s = byTicker.get(t);
    for (const dd of (s?.dates || [])) dateSet.add(dd);
  }
  
  const allDates = Array.from(dateSet).filter((n) => Number.isFinite(n) && n > 0).sort((a, b) => a - b);
  const pointsNeeded = Math.max(3, RANGE_TO_POINTS[range] || 31);
  
  // For rolling metrics, we need lookback data
  const pointsWithLookback = includeLookback ? pointsNeeded + LOOKBACK_DAYS : pointsNeeded;
  const datesSliceWithLookback = allDates.slice(-pointsWithLookback);
  const datesSlice = allDates.slice(-pointsNeeded);
  
  const dateToIndex = new Map<number, number>();
  for (let i = 0; i < allDates.length; i++) dateToIndex.set(allDates[i]!, i);
  
  const sliceIndices: number[] = datesSlice.map((d) => dateToIndex.get(d) ?? -1);
  const sliceIndicesWithLookback: number[] = datesSliceWithLookback.map((d) => dateToIndex.get(d) ?? -1);

  // Align each ticker's data
  const alignedSeries: Record<string, SeriesData> = {};
  const alignedSeriesWithLookback: Record<string, SeriesData> = {};
  
  for (const t of tickers) {
    const s = byTicker.get(t);
    if (!s) continue;
    
    const map = new Map<number, number>();
    for (let i = 0; i < s.dates.length; i++) {
      const k = s.dates[i]!;
      const v = s.values[i]!;
      if (Number.isFinite(k) && Number.isFinite(v)) map.set(k, v);
    }
    
    const alignedFull: number[] = new Array(allDates.length).fill(NaN);
    for (let i = 0; i < allDates.length; i++) {
      const v = map.get(allDates[i]!);
      alignedFull[i] = (typeof v === 'number' && Number.isFinite(v)) ? v : NaN;
    }
    
    const valuesSlice: number[] = sliceIndices.map((idx) => (idx >= 0 ? alignedFull[idx]! : NaN));
    const valuesSliceWithLookback: number[] = sliceIndicesWithLookback.map((idx) => (idx >= 0 ? alignedFull[idx]! : NaN));
    
    alignedSeries[t] = { dates: datesSlice, values: valuesSlice };
    alignedSeriesWithLookback[t] = { dates: datesSliceWithLookback, values: valuesSliceWithLookback };
  }

  return { alignedSeries, alignedSeriesWithLookback, allDates: datesSlice };
}

/**
 * Compute daily returns for multiple tickers
 * NOTE: Returns arrays are kept aligned (same length) with NaN values preserved
 * This is critical for comparative calculations like RS Ratio
 */
export function computeReturnsMap(
  alignedSeries: Record<string, SeriesData>
): Record<string, number[]> {
  const dailyReturnsMap: Record<string, number[]> = {};
  
  for (const [ticker, series] of Object.entries(alignedSeries)) {
    // Keep NaN values to maintain alignment across tickers
    const returns = computeDailyReturns(series.values);
    dailyReturnsMap[ticker] = returns;
  }
  
  return dailyReturnsMap;
}

